/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import LibraryRichText from 'rich-text-to-react';
import Heading from '@tds/core-heading';
import Paragraph from '@tds/core-paragraph';
import UnorderedList from '@tds/core-unordered-list';
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types';

const defaultOptions = isItalicNote => ({
  [BLOCKS.HEADING_1]: (node, key, next) => (
    <Heading key={key} level="h1">
      {next(node.content, key, next)}
    </Heading>
  ),
  [BLOCKS.HEADING_2]: (node, key, next) => (
    <Heading key={key} level="h2">
      {next(node.content, key, next)}
    </Heading>
  ),
  [BLOCKS.HEADING_3]: (node, key, next) => (
    <Heading key={key} level="h3">
      {next(node.content, key, next)}
    </Heading>
  ),
  [BLOCKS.HEADING_4]: (node, key, next) => (
    <Heading key={key} level="h4">
      {next(node.content, key, next)}
    </Heading>
  ),
  [BLOCKS.HEADING_5]: (node, key, next) => (
    <Heading key={key} level="h5">
      {next(node.content, key, next)}
    </Heading>
  ),
  [BLOCKS.PARAGRAPH]: (node, key, next) => {
    const fontStyle = {
      fontStyle: isItalicNote === true ? 'italic' : 'normal',
      fontSize: isItalicNote === true ? '13px' : '100%'
    };
    return (
      <Paragraph
        key={key}
        style={{ lineHeight: '1.5rem', marginBottom: '1em' }}
      >
        <span style={{ ...fontStyle }}>{next(node.content, key, next)}</span>
      </Paragraph>
    );
  },
  [BLOCKS.UL_LIST]: (node, key, next) => (
    <UnorderedList key={key}>{next(node.content, key, next)}</UnorderedList>
  ),
  [BLOCKS.LIST_ITEM]: (node, key, next) => (
    <UnorderedList.Item key={key}>
      {next(node.content, key, next)}
    </UnorderedList.Item>
  ),
  [INLINES.HYPERLINK]: (node, key, next) => (
    <a href={node.data.uri} rel="noopener noreferrer" target="_blank">
      {next(node.content, key, next)}
    </a>
  ),
  [BLOCKS.EMBEDDED_ASSET]: node => {
    const {
      data: {
        target: {
          fields: {
            title,
            file: { url }
          }
        }
      }
    } = node;
    return (
      <img alt={title} key={url} src={url} style={{ marginTop: '2rem' }} />
    );
  }
});

const RichText = ({
  data,
  isItalicNote,
  isColorRed,
  options: { renderMark = {}, renderNode = {} }
}) => {
  const mergedOptions = {
    renderMark: {
      ...renderMark,
      [MARKS.BOLD]: (text, key) => {
        const fontStyle = {
          fontStyle: isItalicNote === true ? 'italic' : 'normal',
          fontSize: isItalicNote === true ? '13px' : '100%',
          color: isColorRed === true ? 'red' : ''
        };
        return (
          <strong key={key} style={{ fontWeight: '900', ...fontStyle }}>
            {text}
          </strong>
        );
      },
      [MARKS.CODE]: (text, key) => (
        <strong key={key} style={{ fontWeight: '900', color: 'red' }}>
          {text}
        </strong>
      ),
      [MARKS.UNDERLINE]: (text, key) => (
        <strong
          key={key}
          style={{
            fontWeight: 'bold',
            textDecoration: 'underline',
            color: 'blue'
          }}
        >
          {text}
        </strong>
      )
    },
    renderNode: {
      ...defaultOptions(isItalicNote),
      ...renderNode
    }
  };

  return data && <LibraryRichText document={data} options={mergedOptions} />;
};

RichText.propTypes = {
  data: PropTypes.shape({
    content: PropTypes.arrayOf(PropTypes.object)
  }),
  isItalicNote: PropTypes.bool,
  isColorRed: PropTypes.bool,
  options: PropTypes.shape({
    renderMark: PropTypes.objectOf(PropTypes.object),
    renderNode: PropTypes.objectOf(PropTypes.object)
  })
};

RichText.defaultProps = {
  data: null,
  isItalicNote: false,
  options: {
    renderMark: {},
    renderNode: {}
  }
};
export default RichText;
